import { defineStore } from 'pinia';
import { UserAttributes } from '@/types/user.types';
import { Pagination } from '@/types/common.types';
import api from '@/services/api.service';
import employeeService, { EmployeeFormData } from '@/services/employee.service';
import { getApiErrorMessage } from '@/utils/apiError';

interface UserState {
  users: UserAttributes[];
  pagination: Pagination | null;
  isLoading: boolean;
  error: string | null;
}

interface FetchUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  roleId?: number;
  status?: 'active' | 'inactive';
}

interface FetchEmployeesParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive';
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    users: [],
    pagination: null,
    isLoading: false,
    error: null,
  }),
  actions: {
    async fetchUsers(params: FetchUsersParams) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await api.get('/users', { params });
        this.users = response.data.data; // Adjust based on your API response structure
        this.pagination = response.data.pagination;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to fetch users');
      } finally {
        this.isLoading = false;
      }
    },
    async fetchEmployees(params: FetchEmployeesParams) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await api.get('/users/employees', { params });
        this.users = response.data.data;
        this.pagination = response.data.pagination;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to fetch employees');
      } finally {
        this.isLoading = false;
      }
    },
    async createEmployee(employeeData: EmployeeFormData) {
      this.isLoading = true;
      this.error = null;
      try {
        const newEmployee = await employeeService.createEmployee(employeeData);
        this.users.unshift(newEmployee);
        return newEmployee;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to create employee');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async updateEmployee(id: string, employeeData: EmployeeFormData) {
      this.isLoading = true;
      this.error = null;
      try {
        const payload = { ...employeeData };
        if (!payload.password) {
          delete payload.password;
        }
        const updatedEmployee = await employeeService.updateEmployee(id, payload);
        const index = this.users.findIndex((user) => user.id === id);
        if (index !== -1) {
          this.users.splice(index, 1, updatedEmployee);
        }
        return updatedEmployee;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to update employee');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
  },
});

